/**
 * localPApi — RTK Query slice cho /purchase/local-p/*.
 * Tag types: 'LocalP' (list) + 'LocalPDetail' (single).
 */
import { baseApi } from './baseApi'
import type {
  DataResponse,
  LocalPCreateIn,
  LocalPDetail,
  LocalPListItem,
  LocalPListQuery,
  LocalPPrintKind,
  LocalPPrintResult,
  LocalPUpdateIn,
  MessageResponse,
  PageResponse,
} from '@/types/api'

export const localPApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listLocalP: b.query<PageResponse<LocalPListItem>, LocalPListQuery | void>({
      query: (params) => ({ url: '/purchase/local-p/list', params: params ?? {} }),
      providesTags: (res) =>
        res
          ? [
              ...res.items.map((it) => ({ type: 'LocalP' as const, id: it.contract_no })),
              { type: 'LocalP' as const, id: 'LIST' },
            ]
          : [{ type: 'LocalP' as const, id: 'LIST' }],
    }),
    getLocalP: b.query<DataResponse<LocalPDetail>, string>({
      query: (contractNo) => `/purchase/local-p/${encodeURIComponent(contractNo)}`,
      providesTags: (_r, _e, contractNo) => [
        { type: 'LocalPDetail', id: contractNo },
      ],
    }),
    createLocalP: b.mutation<DataResponse<LocalPDetail>, LocalPCreateIn>({
      query: (body) => ({
        url: '/purchase/local-p/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'LocalP', id: 'LIST' }, { type: 'ContractAll', id: 'LIST' }],
    }),
    updateLocalP: b.mutation<
      DataResponse<LocalPDetail>,
      { contractNo: string; body: LocalPUpdateIn }
    >({
      query: ({ contractNo, body }) => ({
        url: `/purchase/local-p/${encodeURIComponent(contractNo)}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'LocalP', id: arg.contractNo },
        { type: 'LocalP', id: 'LIST' },
        { type: 'LocalPDetail', id: arg.contractNo },
        { type: 'ContractAll', id: 'LIST' },
      ],
    }),
    deleteLocalP: b.mutation<MessageResponse, string>({
      query: (contractNo) => ({
        url: `/purchase/local-p/${encodeURIComponent(contractNo)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, contractNo) => [
        { type: 'LocalP', id: contractNo },
        { type: 'LocalP', id: 'LIST' },
        { type: 'LocalPDetail', id: contractNo },
        { type: 'ContractAll', id: 'LIST' },
      ],
    }),
    copyLocalP: b.mutation<DataResponse<LocalPDetail>, string>({
      query: (contractNo) => ({
        url: `/purchase/local-p/${encodeURIComponent(contractNo)}/copy`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'LocalP', id: 'LIST' }, { type: 'ContractAll', id: 'LIST' }],
    }),
    forceCloseLocalP: b.mutation<
      DataResponse<LocalPDetail>,
      { contractNo: string; note?: string }
    >({
      query: ({ contractNo, note }) => ({
        url: `/purchase/local-p/${encodeURIComponent(contractNo)}/force-close`,
        method: 'POST',
        body: { note: note ?? '' },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'LocalP', id: arg.contractNo },
        { type: 'LocalP', id: 'LIST' },
        { type: 'LocalPDetail', id: arg.contractNo },
      ],
    }),
    /** Sprint 6 sẽ render thật; Sprint 1 placeholder. */
    printLocalP: b.query<
      DataResponse<LocalPPrintResult>,
      { contractNo: string; kind: LocalPPrintKind }
    >({
      query: ({ contractNo, kind }) =>
        `/purchase/local-p/${encodeURIComponent(contractNo)}/print/${kind}`,
    }),
  }),
})

export const {
  useListLocalPQuery,
  useGetLocalPQuery,
  useCreateLocalPMutation,
  useUpdateLocalPMutation,
  useDeleteLocalPMutation,
  useCopyLocalPMutation,
  useForceCloseLocalPMutation,
  useLazyPrintLocalPQuery,
} = localPApi
